import { useRef, useState } from 'react';
import {
  useAddSubtask, useDeleteSubtask, useDeleteTask, useDepartments, useShiftLocations, useToggleSubtask, useUpdateTask,
} from '../api/hooks';
import type { Employee, Task } from '../api/types';
import { useT } from '../i18n/LangProvider';
import { Icon } from '../ui/Icon';
import { useDialogFocus } from '../ui/useDialogFocus';
import { useConfirm } from '../ui/confirm';

export const PRIORITIES = ['bassa', 'media', 'alta'] as const;
export const RECURRENCES = [
  { id: '', label: 'Nessuna' },
  { id: 'daily', label: 'Ogni giorno' },
  { id: 'weekdays', label: 'Lun–Ven' },
  { id: 'weekly:1', label: 'Ogni lunedì' },
  { id: 'weekly:5', label: 'Ogni venerdì' },
];
export const recurrenceLabel = (r: string | null) => RECURRENCES.find((x) => x.id === (r ?? ''))?.label ?? r;

const splitISO = (iso: string | null) => {
  if (!iso) return { date: '', time: '' };
  const [d, t = ''] = iso.split('T');
  return { date: d, time: t.slice(0, 5) };
};
const joinISO = (date: string, time: string) => (date ? `${date}T${time || '00:00'}` : null);

export function TaskModal({ task, employees, onClose }: { task: Task; employees: Employee[]; onClose: () => void }) {
  const t = useT();
  const confirm = useConfirm();
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus(dialogRef, true, onClose);
  const update = useUpdateTask();
  const del = useDeleteTask();
  const addSub = useAddSubtask();
  const toggleSub = useToggleSubtask();
  const delSub = useDeleteSubtask();
  const deptsQ = useDepartments();
  const officesQ = useShiftLocations();
  const departments = deptsQ.data ?? [];
  const offices = officesQ.data ?? [];

  const init = splitISO(task.dueAt);
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description ?? '');
  const [priority, setPriority] = useState(task.priority || 'media');
  const [dueDate, setDueDate] = useState(init.date);
  const [dueTime, setDueTime] = useState(init.time);
  const [tag, setTag] = useState(task.tag ?? '');
  const [assignMode, setAssignMode] = useState<'person' | 'office' | 'dept'>(
    task.officeKey ? 'office' : task.departmentId ? 'dept' : 'person',
  );
  const [assigneeId, setAssigneeId] = useState(task.assigneeId ?? '');
  const [departmentId, setDepartmentId] = useState(task.departmentId ?? '');
  const [officeKey, setOfficeKey] = useState(task.officeKey ?? '');
  const [recurrence, setRecurrence] = useState(task.recurrence ?? '');
  const [newSub, setNewSub] = useState('');

  const subtasks = task.subtasks ?? [];

  async function save() {
    await update.mutateAsync({
      id: task.id,
      title: title.trim() || task.title,
      description,
      priority,
      dueAt: joinISO(dueDate, dueTime),
      tag,
      assigneeId: assignMode === 'person' ? (assigneeId || null) : null,
      departmentId: assignMode === 'dept' ? (departmentId || null) : null,
      officeKey: assignMode === 'office' ? (officeKey || null) : null,
      recurrence: recurrence || null,
    });
    onClose();
  }

  return (
    <div className="modal-backdrop task-modal-backdrop">
      <div ref={dialogRef} className="modal modal--task task-modal-shell" role="dialog" aria-modal="true" aria-labelledby="task-modal-title" tabIndex={-1}>
        <div className="spaced task-modal__head">
          <strong id="task-modal-title">{task.done ? t('Task completata') : t('Modifica task')}</strong>
          <button className="btn btn--ghost btn--icon" onClick={onClose} aria-label={t('Chiudi')}><Icon name="x" size={16} /></button>
        </div>

        <div className="modal__body task-modal__body">
          <input data-dialog-initial-focus className="input" style={{ fontSize: 16, fontWeight: 600 }} value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('Titolo task')} />

          <div className="grid grid-2" style={{ gap: 14 }}>
            <div className="field">
              <label className="label">{t('Priorità')}</label>
              <div className="role-switch">
                {PRIORITIES.map((p) => (
                  <button key={p} type="button" className={priority === p ? 'is-active' : ''} onClick={() => setPriority(p)}>{t(p)}</button>
                ))}
              </div>
            </div>
            <div className="field">
              <label className="label">{t('Assegnata a')}</label>
              <div className="role-switch" style={{ marginBottom: 6 }}>
                <button type="button" className={assignMode === 'person' ? 'is-active' : ''} onClick={() => setAssignMode('person')}>{t('Persona')}</button>
                <button type="button" className={assignMode === 'office' ? 'is-active' : ''} onClick={() => setAssignMode('office')}>{t('Ufficio')}</button>
                <button type="button" className={assignMode === 'dept' ? 'is-active' : ''} onClick={() => setAssignMode('dept')}>{t('Reparto')}</button>
              </div>
              {assignMode === 'person' ? (
                <select className="input" value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}>
                  <option value="">{t('— nessuno (pool comune) —')}</option>
                  {employees.map((e) => <option key={e.id} value={e.id}>{e.name}{e.role ? ` · ${e.role}` : ''}</option>)}
                </select>
              ) : assignMode === 'office' ? (
                <>
                  <select className="input" value={officeKey} onChange={(e) => setOfficeKey(e.target.value)}>
                    <option value="">{t('— scegli ufficio —')}</option>
                    {offices.map((o) => <option key={o.key} value={o.key}>{o.short ? `${o.short} · ${o.label}` : o.label}</option>)}
                  </select>
                  <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>{t('Visibile solo a chi è in turno in questo ufficio oggi.')}</div>
                </>
              ) : (
                <select className="input" value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
                  <option value="">{t('— scegli reparto —')}</option>
                  {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              )}
            </div>
            <div className="field">
              <label className="label">{t('Scadenza')}</label>
              <div className="row" style={{ gap: 6 }}>
                <input type="date" className="input" value={dueDate} onChange={(e) => setDueDate(e.target.value)} style={{ flex: 1 }} />
                <input type="time" className="input" value={dueTime} onChange={(e) => setDueTime(e.target.value)} disabled={!dueDate} style={{ flex: 1 }} />
                {(dueDate || dueTime) && <button className="btn btn--ghost btn--icon" onClick={() => { setDueDate(''); setDueTime(''); }} title={t('Rimuovi')}><Icon name="x" size={14} /></button>}
              </div>
            </div>
            <div className="field">
              <label className="label">{t('Ricorrenza')}</label>
              <select className="input" value={recurrence} onChange={(e) => setRecurrence(e.target.value)}>
                {RECURRENCES.map((r) => <option key={r.id} value={r.id}>{t(r.label)}</option>)}
              </select>
            </div>
          </div>

          <div className="field">
            <label className="label">{t('Etichetta')}</label>
            <input className="input" value={tag} onChange={(e) => setTag(e.target.value)} placeholder={t('es. Vaticani, Voucher…')} />
          </div>

          <div className="field">
            <label className="label">{t('Sottotask')} {subtasks.length > 0 && <span className="muted">· {subtasks.filter((s) => s.done).length}/{subtasks.length}</span>}</label>
            <div className="col" style={{ gap: 6, marginBottom: 6 }}>
              {subtasks.map((s) => (
                <div key={s.id} className="row" style={{ gap: 8, alignItems: 'center' }}>
                  <button className={`checkbox ${s.done ? 'is-on' : ''}`} onClick={() => toggleSub.mutate({ taskId: task.id, subtaskId: s.id, done: !s.done })}>
                    {s.done && <Icon name="check" size={10} stroke={3} />}
                  </button>
                  <span style={{ flex: 1, fontSize: 13.5, textDecoration: s.done ? 'line-through' : 'none' }}>{s.title}</span>
                  <button className="btn btn--ghost btn--icon" onClick={() => delSub.mutate({ taskId: task.id, subtaskId: s.id })}><Icon name="x" size={13} /></button>
                </div>
              ))}
            </div>
            <form onSubmit={(e) => { e.preventDefault(); if (newSub.trim()) { addSub.mutate({ taskId: task.id, title: newSub.trim() }); setNewSub(''); } }} className="row" style={{ gap: 6 }}>
              <input className="input" value={newSub} onChange={(e) => setNewSub(e.target.value)} placeholder={t('Aggiungi sottotask…')} style={{ flex: 1 }} />
              {newSub && <button type="submit" className="btn btn--sm">{t('Aggiungi')}</button>}
            </form>
          </div>

          <div className="field">
            <label className="label">{t('Descrizione / note')}</label>
            <textarea className="textarea" rows={4} value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('Dettagli, link, riferimenti…')} />
          </div>
        </div>

        <div className="spaced task-modal__foot">
          <button className="btn" style={{ color: 'var(--danger)' }} onClick={async () => { if (await confirm({ message: t('Eliminare questa task?'), confirmLabel: t('Elimina'), danger: true })) { del.mutate(task.id); onClose(); } }}>
            <Icon name="trash" size={13} /> {t('Elimina')}
          </button>
          <div className="cluster">
            <button className="btn" onClick={onClose}>{t('Annulla')}</button>
            <button className="btn btn--brand" onClick={save} disabled={update.isPending}>{t('Salva')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
